//@ts-check
import { useContext, useState } from "react"
import { css } from "@emotion/react"
import Cta from "../_control/Cta"
import MultipleTestCategorySelectionGrid from "../testCategory/MultipleTestCategorySelectionGrid"
import TabBox from "../_layout/TabBox"
import SwitcherSimple from "../_control/SwitcherSimple"
import ProgressBar from "../_general/ProgressBar"
import TextField from "../_control/TextField"
import Aborter from "./Aborter"
import TabNavigator from "./TabNavigator"
import MatchReportContext from "../../context/report-generator/MatchReport"
import { colors } from "shared-ui"
import { lighten } from "polished"
import Loader from "../_general/Loader"
import useFormattedError from "../../hooks/useFormattedError"
import pdfDownloadIcon from '@assets/icons/pdf-download-icon.svg'
import { ReactSVG } from "react-svg"
import { useIntl } from "react-intl"
import FancyDropdown from "../_control/FancyDropdown"
import EventSummary from "../events/EventSummary"
import LocaleContext from "../../context/Locale"
import { format } from "date-fns"
import MultipleIndexCategorySelectionGrid from "../index/MultipleIndexCategorySelectionGrid"
import WysiwygEditor from "../wysiwyg/WysiwygEditor"
import { Link } from "react-router"
import { toZonedTime } from "date-fns-tz"

export default function MatchReportForm({
    reportTypeOptions,
    reportType,
    setReportType,
    ...props
}) {

    const [activeTabId, setActiveTabId] = useState(1)
    const { getErrorMessageFromCode } = useFormattedError()
    const { formatMessage } = useIntl()
    const { datefnsLocale } = useContext(LocaleContext)

    //period
    const {
        testCategories, selectedTestCategories, setSelectedTestCategories,
        relevantIndexes, selectedIndexes, setSelectedIndexes,
        maxSelectedIndexes, maxSelectedCategories, MAX_USER_SELECTED_METRICS,
        allowFetches, abortFetches, handleFetchAllData, fetchingBusyState, dataReady, retryFetchData,
        firstStepValid, secondStepValid,
        reportTitle, setReportTitle, organisationName, setOrganisationName, finalOpponentName, setFinalOpponentName,
        includeCoverPage, setIncludeCoverPage,
        includeContactPage, setIncludeContactPage,
        data,
        note, setNote,
        matchData, matchPickerBusy, usePrefilledMatch, isMatchDeprecated,
        matchOptions, selectedMatch, setSelectedMatch,
        submitReportGenerationRequest,
        pdf,
        printFriendly, setPrintFriendly,
        showBootIndexCheckbox,
        includeBootIndex, setIncludeBootIndex,
    } = useContext(MatchReportContext)

    const timeZone = matchData?.pitch?.venue?.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone

    const formattedTimezonedTime = matchData ?
        format(
            toZonedTime(new Date(matchData.timestamp * 1000), timeZone),
            'PP HH:mm',
            { locale: datefnsLocale }
        )
        :
        ''

    return <TabBox
        onClickTab={(tabId) => { setActiveTabId(tabId) }}
        showHeader={true}
        activeTabId={activeTabId}
        css={css`max-width: 80em;`}
        tabs={[
            {
                id: 1,
                name: formatMessage({ id: 'context' }),
                clickable: (activeTabId > 1),
                children: <div css={style.wrapper}>
                    <h2 css={style.stepTitle}>
                        {formatMessage({ id: 'reportType' })}
                    </h2>
                    <div css={css`
                        display: flex;
                        align-items: center;
                        gap: 0.5em 2em;
                    `}>
                        <FancyDropdown
                            options={reportTypeOptions}
                            value={reportTypeOptions.find(o => o.value === reportType.value)}
                            onChange={v => setReportType(v)}
                            css={css`max-width: 17em; flex-grow: 1;`}
                        />
                        <h4 css={css`color: ${colors.light};`}>
                            {reportType.tooltip}
                        </h4>
                    </div>

                    <hr css={style.divider} />
                    <h2 css={style.stepTitle}>
                        {formatMessage({ id: 'match' })}
                    </h2>
                    <div css={css`display: flex; gap: 1em; align-items: center;`}>
                        {!usePrefilledMatch ?
                            <FancyDropdown
                                options={matchOptions}
                                value={matchOptions.find(o => o.value === selectedMatch?.value)}
                                onChange={v => setSelectedMatch(v)}
                                css={css`max-width: 45em; flex-grow: 1;`}
                            />
                            :
                            (matchData &&
                                <FancyDropdown
                                    options={[]}
                                    disabled={true}
                                    hideArrow={true}
                                    value={{
                                        value: matchData.id,
                                        label: matchData.pitch?.name + ' - ' + formattedTimezonedTime
                                    }}
                                    onChange={() => { }}
                                    css={css`max-width: 45em; flex-grow: 1;`}
                                />
                            )
                        }
                        {matchPickerBusy &&
                            <Loader size="2em" />
                        }
                    </div>

                    {matchData &&
                        <div>
                            {!isMatchDeprecated ?
                                <EventSummary event={matchData} css={css`
                                    max-width: 45em; 
                                    border-radius: 5px;
                                    padding: 1em;
                                    background: rgba(0,0,0,0.125);
                                `} />
                                :
                                <Link
                                    to={`/events?pitch=${matchData.pitch?.id}`}
                                    css={css`text-decoration: underline;`}
                                >
                                    {formatMessage({ id: 'oldMatchNotSupportedMsg' })}
                                </Link>
                            }
                        </div>
                    }

                    <TabNavigator
                        onNextClick={() => { setActiveTabId(2) }}
                        nextStepValid={firstStepValid}
                    />
                </div>,
            },
            {
                id: 2,
                name: formatMessage({ id: 'tests' }),
                clickable: activeTabId > 2,
                children: <div css={style.wrapper}>

                    <h2 css={style.stepTitle}>
                        {formatMessage({ id: 'selectUpToXCategories' }, {  maxNum: MAX_USER_SELECTED_METRICS  })}
                    </h2>
                    <hr css={style.divider} />
                    <h2 css={style.stepTitle}>
                        {formatMessage({ id: 'testCategories' })}
                    </h2>

                    <MultipleTestCategorySelectionGrid
                        testCategories={testCategories}
                        selectedCatIds={selectedTestCategories}
                        setSelectedCatIds={setSelectedTestCategories}
                        includeLab={false}
                        maxSelected={maxSelectedCategories}
                    />
                    <hr css={style.divider} />

                    {!!relevantIndexes.length && <>
                        <h2 css={style.stepTitle}>
                            {formatMessage({ id: 'indexCategories' })}
                        </h2>
                        <MultipleIndexCategorySelectionGrid
                            categories={relevantIndexes}
                            selectedCatIds={selectedIndexes}
                            setSelectedCatIds={setSelectedIndexes}
                            maxSelected={maxSelectedIndexes}
                        />
                    </>}

                    <TabNavigator
                        onPrevClick={() => setActiveTabId(1)}
                        nextStepValid={secondStepValid}
                        onNextClick={() => {
                            allowFetches()
                            handleFetchAllData()
                            setActiveTabId(3)
                        }}
                    />
                </div>,
            },
            {
                id: 3,
                name: formatMessage({ id: 'options' }),
                clickable: (activeTabId > 3),
                children: <div css={style.wrapper}>
                    <h2 css={style.stepTitle}>
                        {fetchingBusyState?.msg}
                        {dataReady &&
                            formatMessage({ id: 'dataReady' })
                        }
                    </h2>
                    <ProgressBar value={fetchingBusyState?.progress ?? 0} />
                    <Aborter abort={abortFetches} />

                    {!data &&
                        <Cta onClick={retryFetchData}>
                            {formatMessage({ id: 'retry' })}
                        </Cta>
                    }

                    <hr css={style.divider} />

                    <h2 css={style.stepTitle}>
                        {formatMessage({ id: 'reportTitle' })}
                    </h2>
                    <TextField
                        value={reportTitle}
                        onChange={(e) => setReportTitle(e.target.value)}
                        css={style.textField}
                    />
                
                    <div css={style.organisationInput}>
                        <TextField
                            value={organisationName}
                            onChange={(e) => setOrganisationName(e.target.value)}
                            css={style.textField}
                        />
                        <span>v</span>
                        <TextField
                            value={finalOpponentName}
                            onChange={(e) => setFinalOpponentName(e.target.value)}
                            css={style.textField}
                        />
                    </div>

                    <h2 css={style.stepTitle}>
                        {formatMessage({ id: 'notes' })}
                    </h2>
                    <WysiwygEditor
                        css={css`width: 100%;`}
                        value={note}
                        onBlur={(value) => { setNote(value) }}
                        height="12em"
                    />

                    <div css={style.optionTogglesGrid}>
                        <SwitcherSimple
                            checked={includeCoverPage}
                            onClick={() => setIncludeCoverPage(curr => !curr)}
                            checkedLabel={formatMessage({ id: 'coverPage' })}
                            css={style.switcher}
                        />
                        {showBootIndexCheckbox &&
                            <SwitcherSimple
                                checked={includeBootIndex}
                                onClick={() => setIncludeBootIndex(curr => !curr)}
                                checkedLabel={formatMessage({ id: 'bootIndex' })}
                                css={style.switcher}
                            />
                        }
                        <SwitcherSimple
                            checked={includeContactPage}
                            onClick={() => setIncludeContactPage(curr => !curr)}
                            checkedLabel={formatMessage({ id: 'contactPage' })}
                            css={style.switcher}
                        />
                    </div>

                    <hr css={style.divider} />
                    <div css={style.optionTogglesGrid}>
                        <SwitcherSimple
                            checked={printFriendly}
                            onClick={() => setPrintFriendly(curr => !curr)}
                            checkedLabel={formatMessage({ id: 'printFriendlyMode' })}
                            css={style.switcher}
                        />
                    </div>

                    <TabNavigator
                        onPrevClick={() => setActiveTabId(2)}
                        nextStepValid={dataReady}
                        onNextClick={() => {
                            setActiveTabId(4)
                            submitReportGenerationRequest()
                        }}
                    />
                </div>,
            },
            {
                id: 4,
                name: formatMessage({ id: 'download' }),
                clickable: activeTabId > 4,
                children: <div css={style.wrapper}>
                    <div css={css`
                        display: grid;
                        min-height: 10em;
                        place-items: center;
                    `}>
                        {pdf.busy &&
                            <h2 css={style.stepTitle}>
                                {formatMessage({ id: 'generatingAndDownloadingPDF' })}
                            </h2>
                        }
                        {pdf.busy ?
                            <Loader size="2em" />
                            :
                            pdf.error ?
                                <div css={css`color: ${colors.warn};`}>
                                    {getErrorMessageFromCode(pdf.error.status)}
                                </div>
                                :
                                <Cta onClick={pdf.download}>
                                    <ReactSVG
                                        src={pdfDownloadIcon}
                                        css={css`width: 50px; height: auto; color: white;`}
                                    />
                                </Cta>
                        }
                    </div>

                    <TabNavigator
                        onPrevClick={() => setActiveTabId(3)}
                    />
                </div>,
            },
        ]
        }
    />
}

const style = {
    wrapper: css` 
        display: grid; 
        gap: 1em; 
    `,
    stepTitle: css`
        text-transform: uppercase;
    `,
    switcher: css`
        max-width: 30em;
    `,
    optionTogglesGrid: css`
        display: grid;
        grid-template-columns: 1fr 1fr;
        @media screen and (max-width: 600px) {
            grid-template-columns: 1fr;
        }
    `,
    organisationInput: css`
        display: grid;
        gap: 1em;
        align-items: center;
        grid-template-columns: 1fr auto 1fr;
    `,
    logo: css`
        width: 3.5em;
        height: auto;
        justify-self: end;
    `,
    textField: css`
        background: ${colors.eventLight}; 
        color: white;

        &:hover {
            background: ${lighten(0.05, colors.eventLight)};
        }
    `,
    divider: css`
        width: 100%;
        opacity: 0.2;
    `
}